import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Save, FileText, Eye, Edit3 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { sanitizeHtml } from '../utils/sanitizeHtml';

const API = process.env.REACT_APP_BACKEND_URL;
function authHeaders() {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

const SLUGS = [
  { slug: 'oferta', label: 'Публичная оферта' },
  { slug: 'privacy', label: 'Политика конфиденциальности' },
  { slug: 'delivery', label: 'Правила доставки' },
];

export default function AdminLegalPages() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeSlug = searchParams.get('slug') || 'oferta';
  const [doc, setDoc] = useState({ title: '', version: '', content_html: '' });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState('edit'); // edit | preview

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/legal-pages/${activeSlug}`, { headers: authHeaders() });
      setDoc(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Не удалось загрузить');
    } finally {
      setLoading(false);
    }
  }, [activeSlug]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!doc.title || !doc.content_html) {
      toast.error('Заполните заголовок и содержимое');
      return;
    }
    setSaving(true);
    try {
      const r = await axios.put(
        `${API}/api/admin/legal-pages/${activeSlug}`,
        { title: doc.title, content_html: doc.content_html, version: doc.version },
        { headers: authHeaders() }
      );
      toast.success(`Сохранено — версия ${r.data.version}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b">
        <div className="max-w-[1200px] mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/ru/admin')} data-testid="legal-admin-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Назад
          </Button>
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-emerald-700" />
            <h1 className="text-xl font-bold">Юридические документы</h1>
          </div>
        </div>
      </header>

      <div className="max-w-[1200px] mx-auto px-4 py-6 space-y-4">
        {/* Slug tabs */}
        <div className="flex flex-wrap gap-2">
          {SLUGS.map(s => (
            <button
              key={s.slug}
              type="button"
              onClick={() => setSearchParams({ slug: s.slug })}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                activeSlug === s.slug
                  ? 'bg-emerald-700 text-white'
                  : 'bg-white border hover:bg-gray-50'
              }`}
              data-testid={`legal-tab-${s.slug}`}
            >
              {s.label}
            </button>
          ))}
        </div>

        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <CardTitle className="text-base">{doc.title || '...'}</CardTitle>
                {doc.version && (
                  <span className="text-xs text-gray-500">v{doc.version}</span>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={mode === 'edit' ? 'default' : 'outline'}
                  onClick={() => setMode('edit')}
                >
                  <Edit3 className="w-3 h-3 mr-1" /> Редактор
                </Button>
                <Button
                  size="sm"
                  variant={mode === 'preview' ? 'default' : 'outline'}
                  onClick={() => setMode('preview')}
                >
                  <Eye className="w-3 h-3 mr-1" /> Предпросмотр
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600">Заголовок</label>
                <Input
                  value={doc.title}
                  onChange={(e) => setDoc({ ...doc, title: e.target.value })}
                  placeholder="Публичная оферта"
                  data-testid="legal-title-input"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">Версия (пусто → авто-дата)</label>
                <Input
                  value={doc.version}
                  onChange={(e) => setDoc({ ...doc, version: e.target.value })}
                  placeholder="2026.05.31"
                  data-testid="legal-version-input"
                />
              </div>
              <div className="flex items-end">
                <Button
                  onClick={save}
                  disabled={saving || loading}
                  className="w-full bg-emerald-700 hover:bg-emerald-800"
                  data-testid="legal-save-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Сохраняю...' : 'Сохранить как новую версию'}
                </Button>
              </div>
            </div>

            <div className="border-t pt-3">
              {mode === 'edit' ? (
                <>
                  <label className="text-xs font-medium text-gray-600">HTML-содержимое</label>
                  <textarea
                    value={doc.content_html}
                    onChange={(e) => setDoc({ ...doc, content_html: e.target.value })}
                    className="w-full font-mono text-xs border rounded-lg p-3 min-h-[500px]"
                    placeholder="<h2>Заголовок</h2><p>Текст...</p>"
                    data-testid="legal-html-textarea"
                  />
                  <p className="text-xs text-gray-400 mt-2">
                    Используйте простые HTML-теги: <code>{'<h2>, <h3>, <p>, <ul>, <li>, <strong>, <em>, <a href="">'}</code>.
                    На сохранение создаётся новая версия (старая помечается неактивной).
                  </p>
                </>
              ) : (
                <article
                  className="bg-white rounded-lg border p-6 prose prose-sm max-w-none"
                  data-testid="legal-preview"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(doc.content_html) || '<em>Пусто</em>' }}
                />
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
